import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { db } from '@/lib/firebase';
import { doc, getDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  MerchantApplicationSettings,
  ApplicationSource,
  ApplicationSourceSettings,
  FieldSetting,
  DEFAULT_INVOICEBUG_SETTINGS,
  DEFAULT_FIELD_DEFINITIONS,
  DEFAULT_DOCUMENT_REQUIREMENTS,
  APPLICATION_SECTIONS,
  DEFAULT_SECTION_CONFIGS,
  DEFAULT_SECTION_ORDER,
  IndustryClassification,
  ClassificationSettings,
} from '../types/fieldSettings';
import type { IndustryClassificationSettings } from '../types/industryClassifications';

export function useMerchantApplicationSettings(
  source?: ApplicationSource,
  selectedSicCode?: string,
  selectedSicDescription?: string
) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<MerchantApplicationSettings | null>(null);
  const [sourceSettings, setSourceSettings] = useState<ApplicationSourceSettings | null>(null);
  const [currentClassification, setCurrentClassification] = useState<IndustryClassification>('low-risk');
  const [classificationSettings, setClassificationSettings] = useState<ClassificationSettings | null>(null);

  // Cache industry classification data to avoid re-fetching on SIC code changes
  const industryClassDataRef = useRef<Record<string, IndustryClassificationSettings>>({});
  const settingsDataRef = useRef<MerchantApplicationSettings | null>(null);

  // Main effect - only reload settings when source changes (NOT on SIC code change)
  useEffect(() => {
    loadSettings();
  }, [user?.companyId, source]);

  // Separate effect - update classification only when SIC code changes (without re-fetching Firestore)
  useEffect(() => {
    // Skip if no SIC code, no source settings, or still loading initial data
    if (!selectedSicCode || !sourceSettings || loading) return;

    const industryClassData = industryClassDataRef.current;
    const sourceName = sourceSettings.source;

    // Determine classification based on cached industry data
    let classification: IndustryClassification = 'low-risk';

    if (industryClassData[sourceName]) {
      const sourceIndustry = industryClassData[sourceName];

      // Check if code is in traditional codes (high-risk)
      // For duplicate codes (same code in both lists), also match by description
      const isTraditional = Object.values(sourceIndustry.traditionalCodes || {}).some(
        (code) => {
          if (code.code !== selectedSicCode || !code.enabled) return false;
          // If description is provided and this code exists in both lists, match by description too
          if (selectedSicDescription) {
            return code.description.toLowerCase() === selectedSicDescription.toLowerCase();
          }
          return true;
        }
      );

      // Check if code is in payfac codes (low-risk)
      // For duplicate codes (same code in both lists), also match by description
      const isPayfac = Object.values(sourceIndustry.payfacCodes || {}).some(
        (code) => {
          if (code.code !== selectedSicCode || !code.enabled) return false;
          // If description is provided and this code exists in both lists, match by description too
          if (selectedSicDescription) {
            return code.description.toLowerCase() === selectedSicDescription.toLowerCase();
          }
          return true;
        }
      );

      if (isTraditional) {
        classification = 'high-risk';
      } else if (isPayfac) {
        classification = 'low-risk';
      }

      console.log('🔄 [SIC CHANGE] SIC Code lookup:', selectedSicCode);
      console.log('🔄 [SIC CHANGE] SIC Description:', selectedSicDescription);
      console.log('🔄 [SIC CHANGE] Is Traditional (high-risk):', isTraditional);
      console.log('🔄 [SIC CHANGE] Is PayFac (low-risk):', isPayfac);
      console.log('🔄 [SIC CHANGE] Final Classification:', classification);
    }

    // Only update classification if it actually changed
    if (classification !== currentClassification) {
      console.log(`🔄 [SIC CHANGE] Classification changed from ${currentClassification} to ${classification}`);
      setCurrentClassification(classification);

      // Re-apply classification-specific settings from cached data
      const data = settingsDataRef.current;
      if (data && sourceSettings) {
        const bankingPartnerId = sourceSettings.bankingPartnerId || data.defaultBankingPartnerId || 'PayArc';
        let bankingPartnerConfig = sourceSettings.bankingPartnerConfigurations?.[bankingPartnerId];

        // Fallback: Try "PayArc" as a key if ID doesn't work
        if (!bankingPartnerConfig && sourceSettings.bankingPartnerConfigurations) {
          bankingPartnerConfig = sourceSettings.bankingPartnerConfigurations['PayArc'];
        }

        let activeClassificationSettings: ClassificationSettings | null = null;

        if (bankingPartnerConfig?.[classification]) {
          activeClassificationSettings = bankingPartnerConfig[classification];
          console.log(`🔄 [SIC CHANGE] Using ${classification} configuration`);
        } else if (sourceSettings.fieldSettings && sourceSettings.documentRequirements) {
          // Fall back to legacy structure
          activeClassificationSettings = {
            fieldSettings: sourceSettings.fieldSettings,
            documentRequirements: sourceSettings.documentRequirements,
            sectionVisibility: sourceSettings.sectionVisibility,
            sectionOrder: sourceSettings.sectionOrder,
            sectionConfigs: sourceSettings.sectionConfigs,
          };
        }

        // Normalize field settings with defaults
        if (activeClassificationSettings) {
          const fieldSettings: Record<string, FieldSetting> = {
            ...(activeClassificationSettings.fieldSettings || {}),
          };

          Object.entries(DEFAULT_FIELD_DEFINITIONS).forEach(([fieldPath, definition]) => {
            if (!fieldSettings[fieldPath]) {
              fieldSettings[fieldPath] = {
                fieldName: fieldPath.split('.').pop() || '',
                fieldPath,
                enabled: true,
                required: true,
                ...definition,
              } as FieldSetting;
            } else if (
              definition.defaultValue !== undefined &&
              fieldSettings[fieldPath].defaultValue === undefined
            ) {
              fieldSettings[fieldPath] = {
                ...fieldSettings[fieldPath],
                defaultValue: definition.defaultValue,
              };
            }
          });

          // Remove Processing History section assignment from low-risk fields
          if (classification === 'low-risk') {
            const processingHistoryFields = Object.keys(fieldSettings).filter(
              fieldPath => fieldPath.startsWith('processingHistory.')
            );

            processingHistoryFields.forEach(fieldPath => {
              if (fieldSettings[fieldPath]) {
                const { section, ...rest } = fieldSettings[fieldPath];
                fieldSettings[fieldPath] = rest as FieldSetting;
              }
            });
          }

          activeClassificationSettings.fieldSettings = fieldSettings;
        }

        setClassificationSettings(activeClassificationSettings);
      }
    }
  }, [selectedSicCode, selectedSicDescription, sourceSettings, loading]);

  const loadSettings = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Load merchant application settings from GLOBAL collection
      const settingsDoc = await getDoc(
        doc(db, 'merchantApplicationSettings', 'config')
      );

      // Load industry classifications from GLOBAL collection
      const industryDoc = await getDoc(
        doc(db, 'merchantIndustryClassifications', 'config')
      );

      let industryClassData: Record<string, IndustryClassificationSettings> = {};
      if (industryDoc.exists()) {
        const data = industryDoc.data();
        industryClassData = data.sources || {};
      }
      // Cache industry classification data for SIC code changes
      industryClassDataRef.current = industryClassData;

      if (settingsDoc.exists()) {
        const data = settingsDoc.data() as MerchantApplicationSettings;

        // Find the source by matching the 'source' property (e.g., 'InvoiceBug')
        // since sources are keyed by document IDs, not by source names
        let selectedSourceKey: string | undefined;
        let sourceCfg: ApplicationSourceSettings | undefined;

        if (source) {
          // Try direct key lookup first (in case it's an ID)
          if (data.sources?.[source]) {
            selectedSourceKey = source;
            sourceCfg = data.sources[source];
          } else {
            // Search for source by matching the 'source' property
            const sourceEntry = Object.entries(data.sources || {}).find(
              ([, cfg]) => cfg.source === source
            );
            if (sourceEntry) {
              selectedSourceKey = sourceEntry[0];
              sourceCfg = sourceEntry[1];
            }
          }
        }

        // Fall back to default source if not found
        if (!sourceCfg) {
          selectedSourceKey = data.defaultSource || Object.keys(data.sources || {})[0];
          sourceCfg = data.sources?.[selectedSourceKey];
        }
        // Determine industry classification from selected SIC code
        let classification: IndustryClassification = 'low-risk';
        if (selectedSicCode && sourceCfg && industryClassData[sourceCfg.source]) {
          const sourceIndustry = industryClassData[sourceCfg.source];

          // Check if code is in traditional codes (high-risk)
          // For duplicate codes (same code in both lists), also match by description
          const isTraditional = Object.values(sourceIndustry.traditionalCodes || {}).some(
            (code) => {
              if (code.code !== selectedSicCode || !code.enabled) return false;
              // If description is provided and this code exists in both lists, match by description too
              if (selectedSicDescription) {
                return code.description.toLowerCase() === selectedSicDescription.toLowerCase();
              }
              return true;
            }
          );

          // Check if code is in payfac codes (low-risk)
          // For duplicate codes (same code in both lists), also match by description
          const isPayfac = Object.values(sourceIndustry.payfacCodes || {}).some(
            (code) => {
              if (code.code !== selectedSicCode || !code.enabled) return false;
              // If description is provided and this code exists in both lists, match by description too
              if (selectedSicDescription) {
                return code.description.toLowerCase() === selectedSicDescription.toLowerCase();
              }
              return true;
            }
          );

          if (isTraditional) {
            classification = 'high-risk';
          } else if (isPayfac) {
            classification = 'low-risk';
          }

          console.log('🔍 [HOOK] SIC Code lookup:', selectedSicCode);
          console.log('🔍 [HOOK] SIC Description:', selectedSicDescription);
          console.log('🔍 [HOOK] Is Traditional (high-risk):', isTraditional);
          console.log('🔍 [HOOK] Is PayFac (low-risk):', isPayfac);
          console.log('🔍 [HOOK] Final Classification:', classification);
        } else {
          console.log('⚠️ [HOOK] No SIC code or source industry data, defaulting to low-risk');
          console.log('⚠️ [HOOK] selectedSicCode:', selectedSicCode);
          console.log('⚠️ [HOOK] sourceCfg?.source:', sourceCfg?.source);
          console.log('⚠️ [HOOK] industryClassData keys:', Object.keys(industryClassData));
        }

        setCurrentClassification(classification);

        // Use source-specific banking partner if configured, otherwise fall back to global default
        const bankingPartnerId = sourceCfg?.bankingPartnerId || data.defaultBankingPartnerId || 'PayArc';

    
        // Try to load classification-specific settings from nested structure
        let activeClassificationSettings: ClassificationSettings | null = null;

        // Try to find the banking partner configuration by ID first, then by common names
        let bankingPartnerConfig = sourceCfg?.bankingPartnerConfigurations?.[bankingPartnerId];

        // Fallback: Try "PayArc" as a key if ID doesn't work
        if (!bankingPartnerConfig && sourceCfg?.bankingPartnerConfigurations) {
          bankingPartnerConfig = sourceCfg.bankingPartnerConfigurations['PayArc'];
          if (bankingPartnerConfig) {
            console.log(`💡 Found config under "PayArc" key instead of ID "${bankingPartnerId}"`);
          }
        }

        if (bankingPartnerConfig?.[classification]) {
          activeClassificationSettings = bankingPartnerConfig[classification];
          console.log(`✅ Using ${classification} configuration for source "${sourceCfg?.source}", banking partner "${bankingPartnerId}"`);
          console.log('📊 Section Visibility Config:', activeClassificationSettings.sectionVisibility);
        } else if (sourceCfg?.fieldSettings && sourceCfg?.documentRequirements) {
          // Fall back to legacy structure
          activeClassificationSettings = {
            fieldSettings: sourceCfg.fieldSettings,
            documentRequirements: sourceCfg.documentRequirements,
            sectionVisibility: sourceCfg.sectionVisibility,
            sectionOrder: sourceCfg.sectionOrder,
            sectionConfigs: sourceCfg.sectionConfigs,
          };
          console.log(`📦 Using legacy flat configuration for source "${sourceCfg?.source}"`);
        } else {
          console.warn(
            `⚠️ No configuration found for source "${sourceCfg?.source}", ` +
            `banking partner "${bankingPartnerId}", classification "${classification}". ` +
            `Will fall back to hardcoded defaults if needed. Please configure settings in the admin panel.`
          );
        }

        // Normalize field settings with defaults
        if (activeClassificationSettings) {
          const fieldSettings: Record<string, FieldSetting> = {
            ...(activeClassificationSettings.fieldSettings || {}),
          };

          Object.entries(DEFAULT_FIELD_DEFINITIONS).forEach(([fieldPath, definition]) => {
            if (!fieldSettings[fieldPath]) {
              fieldSettings[fieldPath] = {
                fieldName: fieldPath.split('.').pop() || '',
                fieldPath,
                enabled: true,
                required: true,
                ...definition,
              } as FieldSetting;
            } else if (
              definition.defaultValue !== undefined &&
              fieldSettings[fieldPath].defaultValue === undefined
            ) {
              fieldSettings[fieldPath] = {
                ...fieldSettings[fieldPath],
                defaultValue: definition.defaultValue,
              };
            }
          });

          // Remove Processing History section assignment from low-risk fields
          if (classification === 'low-risk') {
            const processingHistoryFields = Object.keys(fieldSettings).filter(
              fieldPath => fieldPath.startsWith('processingHistory.')
            );

            processingHistoryFields.forEach(fieldPath => {
              if (fieldSettings[fieldPath]) {
                // Remove section property to prevent the section from appearing
                const { section, ...rest } = fieldSettings[fieldPath];
                fieldSettings[fieldPath] = rest as FieldSetting;
              }
            });

            console.log(`🚫 Removed Processing History section from ${processingHistoryFields.length} fields for low-risk classification`);
          }

          activeClassificationSettings.fieldSettings = fieldSettings;
        }

        setClassificationSettings(activeClassificationSettings);
        setSettings(data);
        setSourceSettings(sourceCfg || DEFAULT_INVOICEBUG_SETTINGS);
        // Cache settings data for SIC code changes
        settingsDataRef.current = data;
      } else {
        // Load application sources from collection to build default settings dynamically
        const sourcesQuery = query(
          collection(db, 'applicationSources'),
          orderBy('order', 'asc')
        );
        const sourcesSnapshot = await getDocs(sourcesQuery);
        const applicationSources = sourcesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          displayName: doc.data().displayName,
          isActive: doc.data().isActive,
        }));

        // Build default settings using IDs from applicationSources collection
        const defaultSourcesMap: Record<string, ApplicationSourceSettings> = {};
        const activeSources = applicationSources.filter(s => s.isActive);

        activeSources.forEach(appSource => {
          defaultSourcesMap[appSource.id] = createDefaultSourceSettings(appSource.name, appSource.displayName);
        });

        // Use first active source ID as default
        const firstSourceId = activeSources[0]?.id || '';

        const defaultSettings: MerchantApplicationSettings = {
          sources: defaultSourcesMap,
          defaultSource: firstSourceId,
          trackingEnabled: true,
        };
        setSettings(defaultSettings);

        // Find source by name or use first source
        let selectedSourceKey = firstSourceId;
        if (source) {
          const matchedSource = activeSources.find(s => s.name === source);
          if (matchedSource) {
            selectedSourceKey = matchedSource.id;
          }
        }

        setSourceSettings(defaultSettings.sources[selectedSourceKey]);

        // Set default classification settings
        const defaultSource = defaultSettings.sources[selectedSourceKey];
        if (defaultSource) {
          setClassificationSettings({
            fieldSettings: defaultSource.fieldSettings,
            documentRequirements: defaultSource.documentRequirements,
            sectionVisibility: defaultSource.sectionVisibility,
            sectionOrder: defaultSource.sectionOrder,
            sectionConfigs: defaultSource.sectionConfigs,
          });
        }
      }
    } catch (error) {
      console.error('Error loading merchant application settings:', error);
      // Fall back to generic defaults on error
      setSourceSettings(DEFAULT_INVOICEBUG_SETTINGS); // Generic fallback
      setClassificationSettings({
        fieldSettings: DEFAULT_INVOICEBUG_SETTINGS.fieldSettings,
        documentRequirements: DEFAULT_INVOICEBUG_SETTINGS.documentRequirements,
        sectionVisibility: DEFAULT_INVOICEBUG_SETTINGS.sectionVisibility,
        sectionOrder: DEFAULT_INVOICEBUG_SETTINGS.sectionOrder,
        sectionConfigs: DEFAULT_INVOICEBUG_SETTINGS.sectionConfigs,
      });
    } finally {
      setLoading(false);
    }
  };

  const createDefaultSourceSettings = (
    source: ApplicationSource,
    displayName: string
  ): ApplicationSourceSettings => {
    const fieldSettings = Object.keys(DEFAULT_FIELD_DEFINITIONS).reduce((acc, fieldPath) => {
      const definition = DEFAULT_FIELD_DEFINITIONS[fieldPath];
      acc[fieldPath] = {
        fieldName: fieldPath.split('.').pop() || '',
        fieldPath,
        ...definition,
        enabled: true,
        required: true,
      };
      return acc;
    }, {} as Record<string, any>);

    return {
      source,
      displayName,
      fieldSettings,
      documentRequirements: [...DEFAULT_DOCUMENT_REQUIREMENTS],
      sectionConfigs: { ...DEFAULT_SECTION_CONFIGS },
      sectionVisibility: {
        [APPLICATION_SECTIONS.PERSONAL_INFO]: true,
        [APPLICATION_SECTIONS.CORPORATION_INFO]: true,
        [APPLICATION_SECTIONS.PROCESSING_HISTORY]: true,
        [APPLICATION_SECTIONS.BENEFICIAL_OWNERS]: true,
        [APPLICATION_SECTIONS.BANK_DETAILS]: true,
        [APPLICATION_SECTIONS.DOCUMENTATION]: true,
      },
      sectionOrder: [...DEFAULT_SECTION_ORDER],
      isCustom: false,
    };
  };

  // Helper function to check if a field should be shown
  const shouldShowField = (fieldPath: string): boolean => {
    if (!classificationSettings?.fieldSettings) {
      return true;
    }

    const fieldSetting = classificationSettings.fieldSettings[fieldPath];
    return fieldSetting ? fieldSetting.enabled : true;
  };

  // Helper function to check if a field is required
  const isFieldRequired = (fieldPath: string): boolean => {
    if (!classificationSettings?.fieldSettings) return true;
    const fieldSetting = classificationSettings.fieldSettings[fieldPath];
    return fieldSetting ? fieldSetting.required : true;
  };

  // Helper function to get default value for a field
  const getFieldDefaultValue = (fieldPath: string, includeWhenVisible: boolean = false): any => {
    if (!classificationSettings?.fieldSettings) return undefined;
    const fieldSetting = classificationSettings.fieldSettings[fieldPath];
    if (!fieldSetting) return undefined;
    if (!fieldSetting.enabled || includeWhenVisible) {
      return fieldSetting.defaultValue;
    }
    return undefined;
  };

  // Helper function to get required documents
  const getRequiredDocuments = (monthlyVolume?: number) => {
    if (!classificationSettings?.documentRequirements) return [];

    return classificationSettings.documentRequirements.filter(doc => {
      if (!doc.enabled) return false;

      // Check if document is required based on volume threshold
      if (doc.processingVolumeThreshold && monthlyVolume) {
        return monthlyVolume >= doc.processingVolumeThreshold;
      }

      return doc.required;
    });
  };

  // Helper function to get all enabled documents
  const getEnabledDocuments = () => {
    if (!classificationSettings?.documentRequirements) return [];
    return classificationSettings.documentRequirements.filter(doc => doc.enabled);
  };

  // Helper function to check if a section should be shown
  const shouldShowSection = (sectionName: string): boolean => {
    if (!classificationSettings?.sectionVisibility) {
      console.log(`⚠️ No sectionVisibility settings found for "${sectionName}", defaulting to visible`);
      return true;
    }
    const isVisible = classificationSettings.sectionVisibility[sectionName] ?? true;

    return isVisible;
  };

  // Helper function to check if a step should be shown based on section visibility
  const shouldShowStep = (stepTitle: string): boolean => {
    // Map step titles to section names
    const stepToSectionMap: Record<string, string> = {
      'Personal Information': APPLICATION_SECTIONS.PERSONAL_INFO,
      'Corporation Info': APPLICATION_SECTIONS.CORPORATION_INFO,
      'Processing History': APPLICATION_SECTIONS.PROCESSING_HISTORY,
      'Beneficial Owners': APPLICATION_SECTIONS.BENEFICIAL_OWNERS,
      'Bank Details': APPLICATION_SECTIONS.BANK_DETAILS,
      'Documentation': APPLICATION_SECTIONS.DOCUMENTATION,
    };

    const sectionName = stepToSectionMap[stepTitle];
    if (!sectionName) {
      console.log(`⚠️ No section mapping found for step "${stepTitle}", defaulting to visible`);
      return true; // Show step if no mapping found
    }

    // Special handling for Documentation step - hide if no documents are enabled
    if (stepTitle === 'Documentation') {
      // Check if section is visible first
      if (!shouldShowSection(sectionName)) {
        console.log(`🚫 Documentation step hidden due to section visibility`);
        return false;
      }

      // Then check if any documents are enabled
      const enabledDocs = getEnabledDocuments();
      const hasEnabledDocs = enabledDocs.length > 0;
      return hasEnabledDocs;
    }

    const shouldShow = shouldShowSection(sectionName);
    return shouldShow;
  };

  // Helper function to get section config
  const getSectionConfig = (sectionName: string) => {
    const sectionConfigs = classificationSettings?.sectionConfigs || DEFAULT_SECTION_CONFIGS;
    return sectionConfigs[sectionName] || DEFAULT_SECTION_CONFIGS[sectionName];
  };

  // Helper function to get section title
  const getSectionTitle = (sectionName: string): string => {
    const config = getSectionConfig(sectionName);
    return config?.title || sectionName;
  };

  // Helper function to get section subtitle
  const getSectionSubtitle = (sectionName: string): string => {
    const config = getSectionConfig(sectionName);
    return config?.subtitle || '';
  };

  // Helper function to get ordered sections
  const getOrderedSections = () => {
    const sectionConfigs = classificationSettings?.sectionConfigs || DEFAULT_SECTION_CONFIGS;
    return Object.entries(sectionConfigs)
      .sort(([, a], [, b]) => (a.order || 999) - (b.order || 999))
      .map(([sectionName]) => sectionName);
  };

  return {
    loading,
    settings,
    sourceSettings,
    classificationSettings,
    currentClassification,
    shouldShowField,
    isFieldRequired,
    getFieldDefaultValue,
    getRequiredDocuments,
    getEnabledDocuments,
    shouldShowSection,
    shouldShowStep,
    getSectionConfig,
    getSectionTitle,
    getSectionSubtitle,
    getOrderedSections,
  };
}
